import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, ShieldCheck } from "lucide-react";
import SignaturePad from "../components/signature/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public token-authenticated signature page.
 *
 * Accessed by the conduttore via a 7-day JWT link generated by the locatore
 * from their user panel. Shows a summary of the immobile + downloadable
 * contract PDF and captures a signature. No account required.
 *
 * Due firme separate: (1) Contratto di locazione — conduttore + eventuali
 * garanti; (2) Attestazione bilaterale — conduttore.
 */
export default function PublicSignaturePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [contrattoSig, setContrattoSig] = useState("");
  const [attestazioneSig, setAttestazioneSig] = useState("");
  // Firma per ciascun garante (sul contratto) — indicizzate per posizione.
  const [garantiSigs, setGarantiSigs] = useState([]);
  const [submitting, setSubmitting] = useState("");
  const [contrattoDone, setContrattoDone] = useState(false);
  const [attestazioneDone, setAttestazioneDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/signature/${token}`);
        setSummary(r.data);
        setContrattoDone(!!r.data.contratto_signed);
        setAttestazioneDone(!!r.data.attestazione_signed);
        const garLen = Array.isArray(r.data.garanti) ? r.data.garanti.length : 0;
        setGarantiSigs(new Array(garLen).fill(""));
      } catch (e) {
        setError(e?.response?.data?.detail || "Link non valido o scaduto.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const isConduttore = summary?.role === "conduttore";
  const garanti = useMemo(
    () => (isConduttore && Array.isArray(summary?.garanti) ? summary.garanti : []),
    [isConduttore, summary]
  );
  const done = contrattoDone && attestazioneDone;

  const submitContratto = async () => {
    const missingGar = garanti.findIndex((_, i) => !garantiSigs[i]);
    if (!contrattoSig) { toast.error("Apponi la firma sul contratto prima di procedere"); return; }
    if (missingGar !== -1) {
      toast.error(`Manca la firma di ${garanti[missingGar]?.nome || `Garante ${missingGar + 1}`}`);
      return;
    }
    setSubmitting("contratto");
    try {
      const body = { signature: contrattoSig, document: "contratto" };
      if (garanti.length > 0) {
        body.garanti_firme = garanti.map((g, i) => ({ nome: g.nome, signature: garantiSigs[i] }));
      }
      await axios.post(`${API}/signature/${token}/sign`, body);
      setContrattoDone(true);
      toast.success("Contratto di locazione firmato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Firma del contratto non salvata");
    } finally { setSubmitting(""); }
  };

  const submitAttestazione = async () => {
    if (!attestazioneSig) { toast.error("Apponi la firma sull'attestazione prima di procedere"); return; }
    setSubmitting("attestazione");
    try {
      await axios.post(`${API}/signature/${token}/sign`, { signature: attestazioneSig, document: "attestazione" });
      setAttestazioneDone(true);
      toast.success("Attestazione bilaterale firmata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Firma dell'attestazione non salvata");
    } finally { setSubmitting(""); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-500" size={28} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-6 text-center" data-testid="public-signature-error">
          <p className="text-red-800 font-medium">Link non valido</p>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  const imm = summary.immobile || {};

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-signature-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <ShieldCheck className="mx-auto text-emerald-600" size={40} />
          <h1 className="text-2xl font-serif text-slate-900 mt-2">
            Firma {isConduttore ? "Conduttore" : "Locatore"} · Unioncasa Milano
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Rivedi i documenti della pratica e apponi la tua firma.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="public-sign-card">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">Riepilogo pratica</h2>
          <div className="grid grid-cols-2 gap-3 text-sm text-slate-700">
            <div><span className="text-slate-500">Immobile:</span> {imm.via} {imm.civico}, {imm.citta || "Milano"}</div>
            <div><span className="text-slate-500">Piano:</span> {imm.piano || "—"}</div>
            <div><span className="text-slate-500">Locatore:</span> {summary.locatore_nome || "—"}</div>
            <div><span className="text-slate-500">Conduttore:</span> {summary.conduttore_nome || "—"}</div>
            {garanti.length > 0 && (
              <div className="col-span-2">
                <span className="text-slate-500">Garanti:</span>{" "}
                {garanti.map((g) => g.nome).join(", ")}
              </div>
            )}
          </div>

          {summary.documenti?.length > 0 && (() => {
            const LABELS = {
              contratto_locazione: "Contratto di locazione",
              attestazione_bilaterale: "Attestazione bilaterale",
              visura_catastale: "Visura catastale",
              planimetria_catastale: "Planimetria catastale",
            };
            const prettyLabel = (dt, filename) => {
              if (LABELS[dt]) return LABELS[dt];
              if (dt?.startsWith("visura_pertinenza_"))
                return "Visura catastale — pertinenza";
              if (dt?.startsWith("planimetria_pertinenza_"))
                return "Planimetria catastale — pertinenza";
              return filename || dt || "Documento";
            };
            const ORDER = [
              "contratto_locazione",
              "attestazione_bilaterale",
              "visura_catastale",
              "planimetria_catastale",
            ];
            const sortKey = (dt) => {
              const idx = ORDER.indexOf(dt);
              if (idx >= 0) return idx;
              if (dt?.startsWith("visura_pertinenza_")) return 10;
              if (dt?.startsWith("planimetria_pertinenza_")) return 11;
              return 20;
            };
            const docs = [...summary.documenti].sort(
              (a, b) => sortKey(a.doc_type) - sortKey(b.doc_type)
            );
            return (
              <div className="mt-5 border-t pt-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">
                  Documenti della pratica
                </h3>
                <ul className="space-y-1.5">
                  {docs.map((doc) => (
                    <li key={doc.doc_type}>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-slate-800 hover:text-slate-900 underline underline-offset-2"
                        data-testid={`public-doc-${doc.doc_type}`}
                      >
                        <FileText size={14} />
                        <span className="font-medium">{prettyLabel(doc.doc_type, doc.filename)}</span>
                        <span className="text-xs text-slate-500">— {doc.filename}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {done && (
            <div className="mt-6 border-t pt-4 text-center" data-testid="public-signature-done">
              <CheckCircle2 className="mx-auto text-emerald-600" size={32} />
              <p className="mt-2 text-emerald-800 font-medium">
                Entrambi i documenti sono stati firmati correttamente.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Puoi chiudere questa pagina. Il locatore e Unioncasa riceveranno conferma.
              </p>
            </div>
          )}

          <SignSection
            step={1}
            title="Firma del Contratto di locazione"
            signed={contrattoDone}
            testId="public-sign-section-contratto"
            unavailable={!summary.has_contratto && "Il contratto di locazione non è ancora disponibile per la firma."}
          >
            <SignaturePad
              label={`Firma del Conduttore${summary.conduttore_nome ? ` — ${summary.conduttore_nome}` : ""}`}
              value={contrattoSig}
              onChange={setContrattoSig}
              testId="public-contratto-pad"
            />
            {garanti.length > 0 && (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Firme dei Garanti</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Passa il dispositivo a ciascun garante presente: le firme vengono apposte sul contratto.
                  </p>
                </div>
                {garanti.map((g, i) => (
                  <div key={i} data-testid={`public-garante-block-${i}`}>
                    <SignaturePad
                      label={`Firma del Garante — ${g.nome}`}
                      value={garantiSigs[i] || ""}
                      onChange={(v) => {
                        const next = [...garantiSigs];
                        next[i] = v;
                        setGarantiSigs(next);
                      }}
                      testId={`public-garante-pad-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <SubmitButton
              onClick={submitContratto}
              loading={submitting === "contratto"}
              disabled={!!submitting || !contrattoSig || garanti.some((_, i) => !garantiSigs[i])}
              testId="public-sign-contratto-submit"
              label={garanti.length > 0 ? "Firma il contratto (conduttore + garanti)" : "Firma il contratto"}
            />
          </SignSection>

          <SignSection
            step={2}
            title="Firma dell'Attestazione bilaterale"
            signed={attestazioneDone}
            testId="public-sign-section-attestazione"
          >
            <SignaturePad
              label={`Firma del Conduttore${summary.conduttore_nome ? ` — ${summary.conduttore_nome}` : ""}`}
              value={attestazioneSig}
              onChange={setAttestazioneSig}
              testId="public-attestazione-pad"
            />
            <SubmitButton
              onClick={submitAttestazione}
              loading={submitting === "attestazione"}
              disabled={!!submitting || !attestazioneSig}
              testId="public-sign-attestazione-submit"
              label="Firma l'attestazione"
            />
          </SignSection>

          {!done && (
            <p className="mt-4 text-[11px] text-slate-500 text-center" data-testid="public-signature-pending-note">
              La firma è completa solo quando entrambi i documenti sono firmati. La firma grafica ha valore
              probatorio; i PDF firmati vengono archiviati da Unioncasa.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SignSection({ step, title, signed, unavailable, testId, children }) {
  return (
    <div className="mt-6 border-t pt-4 space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">{step}. {title}</h3>
        {signed && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`${testId}-signed`}>
            <CheckCircle2 size={14} /> Firmato
          </span>
        )}
      </div>
      {!signed && unavailable && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`${testId}-unavailable`}>
          {unavailable}
        </p>
      )}
      {!signed && !unavailable && children}
    </div>
  );
}

function SubmitButton({ onClick, loading, disabled, testId, label }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="w-full h-11 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black"
      data-testid={testId}
    >
      {loading ? "Salvataggio…" : label}
    </button>
  );
}
